import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { FadeDivider, FormProvider, RHFSubmitBtn, RHFTextField } from '@onesource/components';
import { ChangePasswordSchema, TChangePassword } from '@onesource/schemas';
import { getSupabaseLoginErrorMessage, useChangePasswordMutation } from '@onesource/services';
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { MdLock, MdSave } from 'react-icons/md';

// ----------------------------------------------------------------------

export const SecurityTab: FC = () => {
  const { mutateAsync, isPending, error, reset: resetMutation } = useChangePasswordMutation();

  const methods = useForm<TChangePassword>({
    resolver: zodResolver(ChangePasswordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    mode: 'all',
  });

  const {
    handleSubmit,
    reset,
    formState: { isDirty, isValid },
  } = methods;

  const onSubmit = handleSubmit(async (data: TChangePassword) => {
    resetMutation();
    await mutateAsync(
      { currentPassword: data.currentPassword, newPassword: data.newPassword },
      {
        onSuccess: () => {
          reset();
        },
      },
    );
  });

  return (
    <Box sx={{ pt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <MdLock size={22} />
        <Typography variant="h6" fontWeight={700}>
          Change Password
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Update your password to keep your account secure.
      </Typography>

      <FadeDivider />

      <FormProvider methods={methods} onSubmit={onSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 480 }}>
          {error && (
            <Alert severity="error" onClose={() => resetMutation()}>
              {getSupabaseLoginErrorMessage(error)}
            </Alert>
          )}

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Current Password
            </Typography>
            <RHFTextField name="currentPassword" type="password" placeholder="Enter your current password" />
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              New Password
            </Typography>
            <RHFTextField name="newPassword" type="password" placeholder="Enter a new password (min. 8 characters)" />
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Confirm New Password
            </Typography>
            <RHFTextField name="confirmPassword" type="password" placeholder="Confirm your new password" />
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            pt: 3,
            mt: 3,
            borderTop: theme => `1px solid ${theme.palette.divider}`,
          }}
        >
          <RHFSubmitBtn
            buttonProps={{
              disabled: isPending || !isDirty || !isValid,
              startIcon: isPending ? <CircularProgress size={16} color="inherit" /> : <MdSave />,
            }}
          >
            {isPending ? 'Updating...' : 'Update Password'}
          </RHFSubmitBtn>
        </Box>
      </FormProvider>
    </Box>
  );
};
